import { useMemo } from 'react';
import { ROUTES } from '../../api/routes';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  ContactRound,
  BookOpen,
  DoorOpen,
  BarChart3,
  GraduationCap,
  ClipboardList,
} from 'lucide-react';
import { api } from '../../api/client';
import { useFrequenciaAluno } from '../../api/hooks/usePresencasDiarias';
import { ListAvatar } from '../admin/list-avatar';
import type { Aluno, Turma } from '../../types/models';

interface ResponsavelItem {
  id?: string;
  nome: string;
  parentesco: string;
  principal: boolean;
}

interface BoletimMateria {
  materiaId: string;
  materiaNome: string;
  mediaFinal: number | null;
  bimestres?: { media: number | null }[];
}

function turnoLabel(t?: Turma | null) {
  if (!t) return '—';
  return t.turnoOperacional?.nome || t.turno || '—';
}

function truncate(s: string, max: number) {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Painel lateral do modal Editar Aluno — contexto escolar + responsáveis */
export function AlunoEditSidePanel({
  escolaId,
  alunoId,
  aluno,
  turmaIdForm,
  turmas,
  responsaveis,
  loading,
}: {
  escolaId: string;
  alunoId: string;
  aluno?: Aluno | null;
  turmaIdForm: string;
  turmas?: Turma[];
  responsaveis: ResponsavelItem[];
  loading: boolean;
}) {
  const { frequencia, isLoading: loadingFreq } = useFrequenciaAluno(alunoId);
  const ano = new Date().getFullYear();

  const { data: boletim, isLoading: loadingBoletim } = useQuery({
    queryKey: ['aluno-edit-boletim', escolaId, alunoId, ano],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.alunos.findOne(escolaId, alunoId) + `/boletim?ano=${ano}`);
      return data as { materias?: BoletimMateria[] };
    },
    enabled: !!escolaId && !!alunoId,
    staleTime: 60_000,
  });

  const turmaAtual = useMemo(() => {
    const id = turmaIdForm || aluno?.turmaId;
    if (!id) return aluno?.turma ?? null;
    return turmas?.find((t) => t.id === id) ?? aluno?.turma ?? null;
  }, [turmaIdForm, aluno, turmas]);

  const materias = (boletim?.materias ?? []).filter(
    (m) => m.mediaFinal !== null && m.mediaFinal !== undefined,
  );
  const pct = frequencia.percentual;
  const freqColor = pct >= 85 ? '#047857' : pct >= 75 ? '#b45309' : '#dc2626';

  const respList = responsaveis.filter((r) => r.nome);

  return (
    <div className="ac-modal-dual__side ac-aluno-edit-side">
      <div className="ac-modal-dual__side-panel ac-aluno-edit-side-panel">
        <section className="ac-aluno-edit-side-block">
          <div className="ac-aluno-edit-side-head">
            <GraduationCap size={14} strokeWidth={1.5} />
            <strong>Contexto escolar</strong>
          </div>
          <div className="ac-aluno-edit-side-body">
            {loading ? (
              <p className="ac-aluno-edit-side-muted">Carregando…</p>
            ) : (
              <dl className="ac-aluno-edit-quadro">
                <div className="ac-aluno-edit-quadro-cell">
                  <dt>Matrícula</dt>
                  <dd>{aluno?.matricula || '—'}</dd>
                </div>
                <div className="ac-aluno-edit-quadro-cell">
                  <dt>Situação</dt>
                  <dd>{aluno?.situacao?.replace(/_/g, ' ') || (aluno?.ativo ? 'Ativo' : 'Inativo')}</dd>
                </div>
                <div className="ac-aluno-edit-quadro-cell ac-aluno-edit-quadro-cell--wide">
                  <dt>Turma atual</dt>
                  <dd title={turmaAtual?.nome}>{turmaAtual?.nome || 'Sem turma'}</dd>
                </div>
                <div className="ac-aluno-edit-quadro-cell">
                  <dt>Série / turno</dt>
                  <dd>
                    {turmaAtual?.serie || '—'}
                    {turmaAtual ? ` · ${turnoLabel(turmaAtual)}` : ''}
                  </dd>
                </div>
                <div className="ac-aluno-edit-quadro-cell">
                  <dt>Sala</dt>
                  <dd className="ac-aluno-edit-quadro-val--icon">
                    <DoorOpen size={12} strokeWidth={1.5} aria-hidden />
                    {turmaAtual?.sala?.trim() || 'Não informada'}
                  </dd>
                </div>
              </dl>
            )}
          </div>
        </section>

        <section className="ac-aluno-edit-side-block">
          <div className="ac-aluno-edit-side-head">
            <BarChart3 size={14} strokeWidth={1.5} />
            <strong>Frequência</strong>
          </div>
          <div className="ac-aluno-edit-side-body">
            {loadingFreq ? (
              <p className="ac-aluno-edit-side-muted">Calculando…</p>
            ) : (
              <div className="ac-aluno-edit-freq">
                <span className="ac-aluno-edit-freq-val" style={{ color: freqColor }}>{pct}%</span>
                <span className="ac-aluno-edit-freq-sub">
                  {frequencia.diasPresente}/{frequencia.totalDias} dias presentes
                </span>
                <div
                  className="ac-aluno-edit-freq-bar"
                  role="presentation"
                  aria-hidden
                >
                  <div
                    className="ac-aluno-edit-freq-bar-fill"
                    style={{ width: `${Math.min(100, pct)}%`, background: freqColor }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="ac-aluno-edit-side-block">
          <div className="ac-aluno-edit-side-head">
            <ClipboardList size={14} strokeWidth={1.5} />
            <strong>Notas ({ano})</strong>
          </div>
          <div className="ac-aluno-edit-side-body">
            {loadingBoletim ? (
              <p className="ac-aluno-edit-side-muted">Carregando boletim…</p>
            ) : materias.length === 0 ? (
              <p className="ac-aluno-edit-side-muted">Nenhuma média registrada neste ano.</p>
            ) : (
              <ul className="ac-aluno-edit-notas">
                {materias.slice(0, 6).map((m) => {
                  const v = m.mediaFinal as number;
                  const pctBar = Math.min(100, Math.max(0, (v / 10) * 100));
                  const color = v < 6 ? '#dc2626' : '#16a34a';
                  return (
                    <li key={m.materiaId} className="ac-aluno-edit-nota-row">
                      <span className="ac-aluno-edit-nota-label" title={m.materiaNome}>
                        {truncate(m.materiaNome, 14)}
                      </span>
                      <span className="ac-aluno-edit-nota-track" aria-hidden>
                        <span
                          className="ac-aluno-edit-nota-fill"
                          style={{ width: `${pctBar}%`, background: color }}
                        />
                      </span>
                      <span className="ac-aluno-edit-nota-val" style={{ color }}>
                        {v.toFixed(1)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {materias.length > 6 && (
              <p className="ac-aluno-edit-side-muted ac-aluno-edit-side-muted--tail">
                +{materias.length - 6} disciplina(s) na ficha completa
              </p>
            )}
          </div>
        </section>

        <section className="ac-aluno-edit-side-block ac-aluno-edit-side-block--grow">
          <div className="ac-aluno-edit-side-head">
            <Users size={14} strokeWidth={1.5} />
            <strong>Responsáveis vinculados</strong>
          </div>
          <div className="ac-aluno-edit-side-body ac-aluno-edit-side-body--scroll">
            {loading ? (
              <p className="ac-aluno-edit-side-muted">Carregando vínculos…</p>
            ) : respList.length > 0 ? (
              <ul className="ac-responsavel-alunos-list">
                {respList.map((r, i) => (
                  <li key={r.id || `resp-${i}`} className="ac-responsavel-aluno-item">
                    <ListAvatar fotoUrl={null} nome={r.nome} size={32} />
                    <div className="ac-responsavel-aluno-info">
                      <span className="ac-responsavel-aluno-nome">{r.nome}</span>
                      <span className="ac-responsavel-aluno-turma">
                        {r.parentesco || '—'}
                        {r.principal ? ' · Principal' : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ac-responsavel-alunos-empty ac-responsavel-alunos-empty--compact">
                <ContactRound size={20} strokeWidth={1.5} className="ac-responsavel-alunos-empty-icon" />
                <span>Nenhum responsável vinculado.</span>
                <span className="ac-responsavel-alunos-empty-hint">
                  Use a etapa Responsável no formulário.
                </span>
              </div>
            )}
          </div>
        </section>

        <p className="ac-aluno-edit-side-foot">
          <BookOpen size={11} strokeWidth={1.5} aria-hidden />
          Abra a ficha (ícone olho) para relatórios completos.
        </p>
      </div>
    </div>
  );
}
